import { CHAT_ATTACHMENT_MAX_COUNT, CHAT_ATTACHMENT_STATUS_POLL_MS, postFileToS3Presigned, sleep } from 'api/chat/base-chat';
import { getChatApiBaseUrl, getConversationServiceUrl, isLocalChatApiHost, resolveDirectConversationServiceUrl } from 'config/environment';
import { ApiKey } from 'services/api-key';
export const RL_PROJECT_TEMPLATE_AGENT = 'rl_project_template_agent';
/** SSE / history left_content.type emitted by notify_template_created. */ export const TEMPLATE_CREATED_CONTENT_TYPE = 'template_created';
/**
 * SSE / history left_content.type emitted by propose_template_draft.
 * Carries a full template entity for UI bind — agent does not persist.
 */ export const TEMPLATE_PROPOSAL_CONTENT_TYPE = 'template_proposal';
const isTemplateProposalValue = (value)=>{
    if (!value || typeof value !== 'object') {
        return false;
    }
    const candidate = value;
    return (candidate.mode === 'create' || candidate.mode === 'replace') && candidate.template != null && typeof candidate.template === 'object';
};
export const extractTemplateProposal = (sections)=>{
    let found = null;
    for (const section of sections !== null && sections !== void 0 ? sections : []){
        const left = section === null || section === void 0 ? void 0 : section.left_content;
        if (!(left === null || left === void 0 ? void 0 : left.type)) {
            continue;
        }
        if (left.type === TEMPLATE_PROPOSAL_CONTENT_TYPE) {
            if (isTemplateProposalValue(left.value)) {
                found = left.value;
            }
        }
        if (left.type === 'json') {
            if (left.value == null || typeof left.value !== 'object') {
                continue;
            }
            const value = left.value;
            const event = value.event;
            // Prefer event=template_proposal; also accept bare mode+template
            // (CS may drop custom event keys on some history paths).
            if (isTemplateProposalValue(value) && (event == null || event === TEMPLATE_PROPOSAL_CONTENT_TYPE)) {
                found = {
                    mode: value.mode,
                    template: value.template,
                    summary: value.summary,
                    proposal_id: value.proposal_id
                };
            }
        }
    }
    return found;
};
/**
 * Identity for bind-once. Fingerprints the full proposal template so a later
 * stream frame that fills dates/durations/subtasks/sprints/spaces (same
 * proposal_id) re-applies via maybeApplyProposal.
 */ export const templateProposalBindKey = (proposal)=>{
    var _proposal_proposal_id;
    return `${proposal.mode}:${(_proposal_proposal_id = proposal.proposal_id) !== null && _proposal_proposal_id !== void 0 ? _proposal_proposal_id : ''}:${JSON.stringify(proposal.template)}`;
};
export const extractCreatedTemplateId = (sections)=>{
    for (const section of sections !== null && sections !== void 0 ? sections : []){
        const left = section === null || section === void 0 ? void 0 : section.left_content;
        if (!(left === null || left === void 0 ? void 0 : left.type)) {
            continue;
        }
        if (left.type === TEMPLATE_CREATED_CONTENT_TYPE) {
            const value = left.value;
            const id = Number(value === null || value === void 0 ? void 0 : value.template_id);
            if (Number.isFinite(id) && id > 0) {
                return id;
            }
        }
        if (left.type === 'json') {
            const value = left.value;
            if ((value === null || value === void 0 ? void 0 : value.event) === TEMPLATE_CREATED_CONTENT_TYPE) {
                const id = Number(value.template_id);
                if (Number.isFinite(id) && id > 0) {
                    return id;
                }
            }
        }
    }
    return null;
};
const fetchLocalDevToken = async (baseUrl, userId, accountId)=>{
    const params = new URLSearchParams({
        user_id: String(userId),
        account_id: String(accountId)
    });
    const tokenUrl = `${baseUrl}/token?${params}`;
    let res;
    try {
        res = await fetch(tokenUrl);
    } catch (error) {
        throw new Error(`Cannot reach ${baseUrl} (is the template stack running? ~/Desktop/template-chat-stack.sh start). ${error instanceof Error ? error.message : String(error)}`, {
            cause: error
        });
    }
    if (!res.ok) {
        throw new Error(`GET /token failed: ${res.status}`);
    }
    const data = await res.json();
    if (!data.token) {
        throw new Error('GET /token returned no token');
    }
    return data.token;
};
const buildRequestHeaders = async (auth)=>{
    const directCs = resolveDirectConversationServiceUrl();
    if (directCs) {
        const token = await fetchLocalDevToken(directCs, auth.userId, auth.accountId);
        return {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        };
    }
    return {
        'Content-Type': 'application/json',
        'initiator-source': 'UI',
        ...ApiKey.header()
    };
};
const readErrorMessage = async (res)=>{
    var _ref, _ref1, _ref2;
    var _body_detail;
    const body = await res.json().catch(()=>null);
    const detailMessage = typeof (body === null || body === void 0 ? void 0 : body.detail) === 'string' ? body.detail : body === null || body === void 0 ? void 0 : (_body_detail = body.detail) === null || _body_detail === void 0 ? void 0 : _body_detail.error_message;
    return (_ref = (_ref1 = (_ref2 = body === null || body === void 0 ? void 0 : body.error_message) !== null && _ref2 !== void 0 ? _ref2 : detailMessage) !== null && _ref1 !== void 0 ? _ref1 : body === null || body === void 0 ? void 0 : body.message) !== null && _ref !== void 0 ? _ref : `Request failed: ${res.status}`;
};
const sendMessage = async (params)=>{
    var _params_conversationMetaData, _data_conversation_id, _data_token, _data_success, _data_error_message;
    const headers = await buildRequestHeaders({
        userId: params.userId,
        accountId: params.accountId
    });
    const conversation_metadata = {
        agent_name: RL_PROJECT_TEMPLATE_AGENT,
        created_by_user_id: params.userId
    };
    if (params.createFlow) {
        conversation_metadata.create_flow = true;
    } else if (params.templateId != null && params.templateId > 0) {
        conversation_metadata.rl_template_id = params.templateId;
    }
    if (params.canvasSketch != null) {
        conversation_metadata.rl_canvas_sketch = params.canvasSketch;
    }
    const payload = {
        conversation_id: params.conversationId,
        account_id: params.accountId,
        user_id: params.userId,
        visibility: 'public',
        message: {
            sections: params.messageSections,
            message_metadata: {
                ...(_params_conversationMetaData = params.conversationMetaData) !== null && _params_conversationMetaData !== void 0 ? _params_conversationMetaData : {},
                ...params.canvasSketch != null ? {
                    rl_canvas_sketch: params.canvasSketch
                } : {}
            }
        },
        conversation_metadata
    };
    const res = await fetch(`${getChatApiBaseUrl()}/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
    });
    if (!res.ok) {
        throw new Error(await readErrorMessage(res));
    }
    const data = await res.json();
    return {
        conversation_id: (_data_conversation_id = data.conversation_id) !== null && _data_conversation_id !== void 0 ? _data_conversation_id : params.conversationId,
        message_id: data.message_id,
        chat_stream_id: data.chat_stream_id,
        token: (_data_token = data.token) !== null && _data_token !== void 0 ? _data_token : '',
        success: (_data_success = data.success) !== null && _data_success !== void 0 ? _data_success : true,
        error_message: (_data_error_message = data.error_message) !== null && _data_error_message !== void 0 ? _data_error_message : null
    };
};
const getConversations = async (templateId, auth)=>{
    const params = new URLSearchParams({
        page: '1',
        page_size: '1',
        agent_name: RL_PROJECT_TEMPLATE_AGENT,
        meta_data__rl_template_id: String(templateId),
        meta_data__created_by_user_id: String(auth.userId)
    });
    const headers = await buildRequestHeaders(auth);
    const res = await fetch(`${getChatApiBaseUrl()}/chat/conversations?${params}`, {
        headers
    });
    if (!res.ok) {
        throw new Error(await readErrorMessage(res));
    }
    return res.json();
};
const getConversationHistory = async (conversationId, auth, page = 1, pageSize = 50)=>{
    const params = new URLSearchParams({
        conversation_id: conversationId,
        page: String(page),
        page_size: String(pageSize)
    });
    const headers = await buildRequestHeaders(auth);
    const res = await fetch(`${getChatApiBaseUrl()}/chat/conversations/${conversationId}?${params}`, {
        headers
    });
    if (!res.ok) {
        throw new Error(await readErrorMessage(res));
    }
    return res.json();
};
const getStreamUrl = (chatStreamId)=>{
    const directCs = resolveDirectConversationServiceUrl();
    if (directCs) {
        return `${directCs}/api/v1/chat/stream/${chatStreamId}`;
    }
    // POST goes via rocket-api proxy; SSE stream uses conversation-service directly with bearer token.
    return `${getConversationServiceUrl()}/api/v1/chat/stream/${chatStreamId}`;
};
/**
 * Same host as template chat send/history (Nitro path):
 * `/chat/attachment/{id}` → CS → S3. Uses getChatApiBaseUrl so local CS
 * override and rocket-api proxy stay aligned with chat.
 */ const uploadFiles = async (params)=>{
    var _presigned_presigned_urls, _presigned_expiration_seconds;
    if (!params.conversationId) {
        throw new Error('Conversation ID is required for file upload');
    }
    if (params.files.length === 0) {
        return [];
    }
    if (params.files.length > CHAT_ATTACHMENT_MAX_COUNT) {
        throw new Error(`Cannot upload more than ${CHAT_ATTACHMENT_MAX_COUNT} files at once`);
    }
    const auth = {
        userId: params.userId,
        accountId: params.accountId
    };
    const headers = await buildRequestHeaders(auth);
    const chatBase = getChatApiBaseUrl();
    const attachmentBase = `${chatBase}/chat/attachment/${params.conversationId}`;
    const presignRes = await fetch(`${attachmentBase}?count=${params.files.length}`, {
        headers
    });
    if (!presignRes.ok) {
        throw new Error(await readErrorMessage(presignRes));
    }
    const presigned = await presignRes.json();
    const slots = (_presigned_presigned_urls = presigned.presigned_urls) !== null && _presigned_presigned_urls !== void 0 ? _presigned_presigned_urls : [];
    if (slots.length < params.files.length) {
        throw new Error('Not enough presigned upload URLs returned');
    }
    await Promise.all(params.files.map(async (file, index)=>{
        var _slot_fields;
        const slot = slots[index];
        if (!slot) {
            throw new Error(`Missing presigned URL for file ${file.name}`);
        }
        if (typeof presigned.max_file_size_bytes === 'number' && file.size > presigned.max_file_size_bytes) {
            throw new Error(`${file.name} exceeds the ${presigned.max_file_size_mb}MB size limit`);
        }
        if (typeof presigned.min_file_size_bytes === 'number' && file.size < presigned.min_file_size_bytes) {
            throw new Error(`${file.name} is below the minimum file size`);
        }
        await postFileToS3Presigned(slot.url, (_slot_fields = slot.fields) !== null && _slot_fields !== void 0 ? _slot_fields : {}, file);
    }));
    const timeoutMs = Math.max(((_presigned_expiration_seconds = presigned.expiration_seconds) !== null && _presigned_expiration_seconds !== void 0 ? _presigned_expiration_seconds : 60) * 1000, CHAT_ATTACHMENT_STATUS_POLL_MS * 2);
    return Promise.all(params.files.map(async (file, index)=>{
        var _slots_index;
        const fileKey = (_slots_index = slots[index]) === null || _slots_index === void 0 ? void 0 : _slots_index.key;
        if (!fileKey) {
            throw new Error(`Missing file key for ${file.name}`);
        }
        const deadline = Date.now() + timeoutMs;
        while(Date.now() < deadline){
            const statusRes = await fetch(`${attachmentBase}/status?file_key=${encodeURIComponent(fileKey)}`, {
                headers
            });
            // Local CS may not expose /status yet; S3 PUT already succeeded.
            if (statusRes.status === 404 && isLocalChatApiHost(chatBase)) {
                return {
                    fileKey,
                    fileName: file.name
                };
            }
            if (!statusRes.ok) {
                throw new Error(await readErrorMessage(statusRes));
            }
            const status = await statusRes.json();
            if (status.uploaded) {
                return {
                    fileKey,
                    fileName: file.name
                };
            }
            await sleep(CHAT_ATTACHMENT_STATUS_POLL_MS);
        }
        throw new Error(`Timed out waiting for attachment upload status for ${fileKey}`);
    }));
};
/**
 * Rebind a create-flow conversation onto a saved template so edit chat
 * `getConversations(templateId)` finds the same history. CS deep-merges
 * metadata — set create_flow false so later turns are edit mode.
 */ const bindConversationToTemplate = async (params)=>{
    if (!params.conversationId || params.templateId <= 0) {
        throw new Error('conversationId and templateId are required to bind chat');
    }
    const headers = await buildRequestHeaders({
        userId: params.userId,
        accountId: params.accountId
    });
    const res = await fetch(`${getChatApiBaseUrl()}/chat/conversations/${params.conversationId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
            metadata: {
                rl_template_id: params.templateId,
                create_flow: false,
                created_by_user_id: params.userId,
                agent_name: RL_PROJECT_TEMPLATE_AGENT
            }
        })
    });
    if (!res.ok) {
        throw new Error(await readErrorMessage(res));
    }
};
export const TemplateChatAPI = {
    sendMessage,
    getConversations,
    getConversationHistory,
    getStreamUrl,
    uploadFiles,
    bindConversationToTemplate
};
